/**
 * @fileoverview Lightweight external store that tracks "new" order IDs
 * so OrderGroupCard can show a temporary highlight badge.
 * Auto-clears after a configurable duration.
 * @module features/daily-summary/hooks/newOrderHighlights
 */

import { useSyncExternalStore } from 'react'

let _ids = new Set<string>()
const _listeners = new Set<() => void>()
let _timer: ReturnType<typeof setTimeout> | null = null

function notify(): void {
  _listeners.forEach((fn) => fn())
}

/** Mark a set of orderIds as "new" — auto-clears after `durationMs`. */
export function highlightNewOrders(
  orderIds: string[],
  durationMs = 15_000,
): void {
  _ids = new Set(orderIds)
  notify()

  if (_timer) clearTimeout(_timer)
  _timer = setTimeout(() => {
    _ids = new Set()
    notify()
  }, durationMs)
}

/** Subscribe to the current set of highlighted (new) order IDs. */
export function useNewOrderHighlights(): Set<string> {
  return useSyncExternalStore(
    (cb) => {
      _listeners.add(cb)
      return () => {
        _listeners.delete(cb)
      }
    },
    () => _ids,
  )
}
